import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { db } from "@lib/firebase";
import {
  collection,
  query,
  onSnapshot,
  where,
  limit,
  startAfter,
  orderBy,
  getDocs,
  doc,
  getDoc,
} from "firebase/firestore";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faClock,
  faCheckCircle,
  faSearch,
  faSortUp,
  faSortDown,
  faChevronDown,
} from "@fortawesome/free-solid-svg-icons";

const PAGE_SIZE = 50;

const SkeletonRow = React.memo(() => (
  <tr className="border-b border-gray-200">
    <td className="px-4 py-4">
      <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
    </td>
    <td className="px-4 py-4 hidden md:table-cell">
      <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
    </td>
    <td className="px-4 py-4">
      <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
    </td>
    <td className="px-4 py-4">
      <div className="h-4 bg-gray-200 rounded animate-pulse w-4"></div>
    </td>
  </tr>
));

const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
};

const CategoryButton = React.memo(({ category, selectedCategory, onClick }) => (
  <button
    onClick={() => onClick(category.distance)}
    className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
      selectedCategory === category.distance
        ? "bg-lime-600 text-white"
        : "bg-gray-200 text-gray-700 hover:bg-gray-300"
    }`}
    aria-label={`Filter participants by category ${category.distance} KM`}
  >
    {category.distance === "all" ? "ALL" : `${category.distance} KM`}
  </button>
));

export const EntryListPage = React.memo(() => {
  const [state, setState] = useState({
    participants: [],
    allParticipants: [],
    filteredParticipants: [],
    fullFilteredList: [],
    searchTerm: "",
    eventTitle: "JOTR",
    selectedCategory: "all",
    categories: [{ distance: "all", description: "ALL" }],
    loading: true,
    loadingMore: false,
    error: null,
    collectionName: "pendaftaranJOTR",
    lastVisible: null,
    totalParticipants: 0,
    totalFiltered: 0,
    sortGenderDirection: null,
    hasMore: true,
    searchPageIndex: 0,
    categoriesLoaded: false,
  });
  const isMounted = useRef(true);
  const unsubscribeRef = useRef({
    participants: null,
    total: null,
  });
  const searchPageIndexRef = useRef(0);

  const debouncedSearchTerm = useDebounce(state.searchTerm, 300);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      Object.values(unsubscribeRef.current).forEach((unsubscribe) => {
        if (unsubscribe) unsubscribe();
      });
    };
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      console.log("Fetching categories for JOTR");
      const eventRef = doc(db, "semuaKegiatan", "JOTR");
      const eventDoc = await getDoc(eventRef);
      if (eventDoc.exists()) {
        const rawCategories = eventDoc.data().categories || [];
        // Combine categories with distance 20
        const combined20K = rawCategories
          .filter((cat) => cat.distance === 20)
          .map((cat) => cat.categoryName);
        const otherCategories = rawCategories
          .filter((cat) => cat.distance !== 20)
          .map((cat) => ({
            distance: cat.distance.toString().trim(),
            description: cat.description || `${cat.distance} KM`,
            categoryName: cat.categoryName || `${cat.distance} KM`,
          }));
        // Sort categories by distance numerically, keeping "all" first
        const sortedCategories = [
          { distance: "all", description: "ALL" },
          ...[
            ...otherCategories,
            ...(combined20K.length > 0
              ? [
                  {
                    distance: "20",
                    description: "Classic Distance & Mix Group (20K)",
                    categoryName: combined20K,
                  },
                ]
              : []),
          ].sort((a, b) => {
            const aDistance = parseInt(a.distance, 10);
            const bDistance = parseInt(b.distance, 10);
            return aDistance - bDistance;
          }),
        ];
        if (isMounted.current) {
          setState((prev) => ({
            ...prev,
            categories: sortedCategories,
            categoriesLoaded: true,
            loading: true,
          }));
        }
      } else {
        throw new Error("JOTR document not found in allActivities");
      }
    } catch (err) {
      if (isMounted.current) {
        console.error("Error fetching categories:", err.message);
        setState((prev) => ({
          ...prev,
          error: `Failed to load JOTR categories: ${err.message}`,
          loading: false,
        }));
      }
    }
  }, []);

  const mapCategoryToFirestore = useCallback(
    (distance) => {
      if (distance === "all") return null;
      const category = state.categories.find(
        (cat) => cat.distance === distance
      );
      const kategori = category ? category.categoryName : null;
      console.log(`Mapping distance ${distance} to kategori: ${kategori}`);
      return kategori;
    },
    [state.categories]
  );

  const loadPage = useCallback(
    (collectionName, lastDoc, sortDirection, selectedDistance) => {
      if (!collectionName || !state.categoriesLoaded) {
        setState((prev) => ({ ...prev, loading: false, loadingMore: false }));
        return;
      }

      setState((prev) => ({
        ...prev,
        loading: !lastDoc,
        loadingMore: !!lastDoc,
        participants: lastDoc ? prev.participants : [],
        filteredParticipants: debouncedSearchTerm
          ? prev.filteredParticipants
          : [],
      }));

      const participantsCollection = collection(db, collectionName);
      const baseConstraints = [
        where("transaction_status", "in", ["settlement", "pending"]),
      ];
      const firestoreCategory = mapCategoryToFirestore(selectedDistance);
      if (firestoreCategory) {
        if (Array.isArray(firestoreCategory)) {
          baseConstraints.push(where("kategori", "in", firestoreCategory));
        } else {
          baseConstraints.push(where("kategori", "==", firestoreCategory));
        }
      }
      const orderConstraints = [];
      if (sortDirection) {
        orderConstraints.push(orderBy("gender", sortDirection));
      }
      orderConstraints.push(orderBy("transaction_status"));
      orderConstraints.push(orderBy("namaLengkap"));

      const qParticipants = lastDoc
        ? query(
            participantsCollection,
            ...baseConstraints,
            ...orderConstraints,
            startAfter(lastDoc),
            limit(PAGE_SIZE)
          )
        : query(
            participantsCollection,
            ...baseConstraints,
            ...orderConstraints,
            limit(PAGE_SIZE)
          );

      console.log(
        `Querying participants for distance: ${selectedDistance}, constraints: ${
          firestoreCategory
            ? `kategori=${firestoreCategory}`
            : "no kategori filter"
        }`
      );

      const unsubscribe = onSnapshot(
        qParticipants,
        (snapshot) => {
          if (!isMounted.current) return;
          console.log(
            `Participants snapshot for distance ${selectedDistance}: count=${snapshot.size}, empty=${snapshot.empty}`
          );
          if (snapshot.empty) {
            setState((prev) => ({
              ...prev,
              participants: debouncedSearchTerm ? prev.participants : [],
              filteredParticipants: debouncedSearchTerm
                ? prev.filteredParticipants
                : [],
              lastVisible: null,
              loading: false,
              loadingMore: false,
              hasMore: false,
            }));
            return;
          }

          const participantList = snapshot.docs.map((doc) => {
            const data = doc.data();
            console.log(
              `Participant data: id=${doc.id}, kategori=${
                data.kategori
              }, teamName=${data.teamName || "N/A"}`
            );
            return {
              id: doc.id,
              nama:
                data.teamName && data.kategori === "Mix Group (20K)"
                  ? data.teamName
                  : data.namaLengkap || "Name not available",
              gender: data.gender || "Unknown",
              kota: data.kota || data.alamat || "Unknown",
              statusPembayaran: data.transaction_status || "pending",
              category: data.kategori || "Unknown",
              docRef: doc,
            };
          });

          setState((prev) => {
            const updatedParticipants = lastDoc
              ? [...prev.participants, ...participantList]
              : participantList;
            return {
              ...prev,
              participants: updatedParticipants,
              filteredParticipants: debouncedSearchTerm
                ? prev.filteredParticipants
                : updatedParticipants,
              lastVisible:
                snapshot.docs.length > 0
                  ? snapshot.docs[snapshot.docs.length - 1]
                  : null,
              loading: false,
              loadingMore: false,
              totalFiltered: debouncedSearchTerm
                ? prev.totalFiltered
                : prev.totalParticipants,
              hasMore: snapshot.size === PAGE_SIZE,
            };
          });
        },
        (err) => {
          if (!isMounted.current) return;
          console.error(
            `Firestore error in loadPage for distance ${selectedDistance}:`,
            err.message
          );
          setState((prev) => ({
            ...prev,
            ...prev,
            error: `Failed to load participant data: ${err.message}. Please check Firestore index or contact admin.`,
            loading: false,
            loadingMore: false,
          }));
        }
      );

      unsubscribeRef.current.participants = unsubscribe;
    },
    [debouncedSearchTerm, state.categoriesLoaded, mapCategoryToFirestore]
  );

  const fetchAllParticipants = useCallback(
    async (collectionName, selectedDistance) => {
      if (!collectionName || !state.categoriesLoaded) return;
      try {
        const participantsCollection = collection(db, collectionName);
        const baseConstraints = [
          where("transaction_status", "in", ["settlement", "pending"]),
        ];
        const firestoreCategory = mapCategoryToFirestore(selectedDistance);
        if (firestoreCategory) {
          if (Array.isArray(firestoreCategory)) {
            baseConstraints.push(where("kategori", "in", firestoreCategory));
          } else {
            baseConstraints.push(where("kategori", "==", firestoreCategory));
          }
        }
        const q = query(participantsCollection, ...baseConstraints);
        console.log(
          `Fetching all participants for distance: ${selectedDistance}, constraints: ${
            firestoreCategory
              ? `kategori=${firestoreCategory}`
              : "no kategori filter"
          }`
        );
        const snapshot = await getDocs(q);
        console.log(
          `All participants snapshot for distance ${selectedDistance}: count=${snapshot.size}, empty=${snapshot.empty}`
        );
        const allParticipants = snapshot.docs.map((doc) => {
          const data = doc.data();
          console.log(
            `Participant data: id=${doc.id}, kategori=${
              data.kategori
            }, teamName=${data.teamName || "N/A"}`
          );
          return {
            id: doc.id,
            nama:
              data.teamName && data.kategori === "Mix Group (20K)"
                ? data.teamName
                : data.namaLengkap || "Name not available",
            gender: data.gender || "Unknown",
            kota: data.kota || data.alamat || "Unknown",
            statusPembayaran: data.transaction_status || "pending",
            category: data.kategori || "Unknown",
            docRef: doc,
          };
        });
        if (isMounted.current) {
          setState((prev) => ({
            ...prev,
            allParticipants,
            totalParticipants: snapshot.size,
            totalFiltered: debouncedSearchTerm
              ? prev.totalFiltered
              : snapshot.size,
          }));
        }
      } catch (err) {
        if (isMounted.current) {
          console.error(
            `Firestore error in fetchAllParticipants for distance ${selectedDistance}:`,
            err.message
          );
          setState((prev) => ({
            ...prev,
            ...prev,
            error: `Failed to load all participants: ${err.message}. Please contact admin.`,
          }));
        }
      }
    },
    [debouncedSearchTerm, state.categoriesLoaded, mapCategoryToFirestore]
  );

  const handleCategoryChange = useCallback(
    (distance) => {
      if (distance === state.selectedCategory) return;
      console.log(`Changing category to: ${distance}`);
      setState((prev) => ({
        ...prev,
        selectedCategory: distance,
        participants: [],
        filteredParticipants: [],
        fullFilteredList: [],
        lastVisible: null,
        hasMore: true,
        searchTerm: "",
        totalFiltered: 0,
      }));
      searchPageIndexRef.current = 0;
      if (state.collectionName && state.categoriesLoaded) {
        fetchAllParticipants(state.collectionName, distance);
        loadPage(
          state.collectionName,
          null,
          state.sortGenderDirection,
          distance
        );
      }
    },
    [
      fetchAllParticipants,
      loadPage,
      state.collectionName,
      state.sortGenderDirection,
      state.categoriesLoaded,
      state.selectedCategory,
    ]
  );

  useEffect(() => {
    console.log("Initializing data for JOTR");
    fetchCategories();
  }, [fetchCategories]);

  useEffect(() => {
    if (state.categoriesLoaded && state.collectionName) {
      fetchAllParticipants(state.collectionName, state.selectedCategory);
      loadPage(
        state.collectionName,
        null,
        state.sortGenderDirection,
        state.selectedCategory
      );
    }
  }, [
    state.categoriesLoaded,
    state.collectionName,
    state.selectedCategory,
    state.sortGenderDirection,
    fetchAllParticipants,
    loadPage,
  ]);

  useEffect(() => {
    if (!state.collectionName || !state.categoriesLoaded) return;
    const participantsCollection = collection(db, state.collectionName);
    const baseConstraints = [
      where("transaction_status", "in", ["settlement", "pending"]),
    ];
    const firestoreCategory = mapCategoryToFirestore(state.selectedCategory);
    if (firestoreCategory) {
      if (Array.isArray(firestoreCategory)) {
        baseConstraints.push(where("kategori", "in", firestoreCategory));
      } else {
        baseConstraints.push(where("kategori", "==", firestoreCategory));
      }
    }
    const q = query(participantsCollection, ...baseConstraints);
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        if (!isMounted.current) return;
        console.log(
          `Total count for distance ${state.selectedCategory}: count=${snapshot.size}`
        );
        setState((prev) => ({
          ...prev,
          totalParticipants: snapshot.size,
          totalFiltered: debouncedSearchTerm
            ? prev.totalFiltered
            : snapshot.size,
        }));
      },
      (err) => {
        if (!isMounted.current) return;
        console.error(
          `Firestore error in total count for distance ${state.selectedCategory}:`,
          err.message
        );
      }
    );
    unsubscribeRef.current.total = unsubscribe;
    return () => unsubscribe();
  }, [
    state.collectionName,
    state.selectedCategory,
    debouncedSearchTerm,
    state.categoriesLoaded,
  ]);

  useEffect(() => {
    if (!state.collectionName || !state.categoriesLoaded) return;

    if (debouncedSearchTerm) {
      const filteredList = state.allParticipants.filter((p) => {
        const nama = p.nama || "";
        return nama
          .toLowerCase()
          .includes(debouncedSearchTerm.toLowerCase().trim());
      });

      setState((prev) => {
        const initialParticipants = filteredList.slice(0, PAGE_SIZE);
        if (
          JSON.stringify(prev.filteredParticipants) ===
            JSON.stringify(initialParticipants) &&
          prev.totalFiltered === filteredList.length &&
          prev.fullFilteredList.length === filteredList.length
        ) {
          return prev;
        }
        searchPageIndexRef.current = 0;
        return {
          ...prev,
          filteredParticipants: initialParticipants,
          fullFilteredList: filteredList,
          totalFiltered: filteredList.length,
          hasMore: filteredList.length > PAGE_SIZE,
          lastVisible: null,
          searchPageIndex: 0,
        };
      });
    } else {
      setState((prev) => {
        const newParticipants =
          prev.participants.length > 0 ? prev.participants : [];
        const newLastVisible =
          prev.participants.length > 0 &&
          prev.participants[prev.participants.length - 1]?.docRef
            ? prev.participants[prev.participants.length - 1].docRef
            : null;
        if (
          JSON.stringify(prev.filteredParticipants) ===
            JSON.stringify(newParticipants) &&
          prev.totalFiltered === prev.totalParticipants &&
          prev.lastVisible === newLastVisible
        ) {
          return prev;
        }
        searchPageIndexRef.current = 0;
        return {
          ...prev,
          filteredParticipants: newParticipants,
          fullFilteredList: [],
          totalFiltered: prev.totalParticipants,
          hasMore: newParticipants.length < prev.totalParticipants,
          lastVisible: newLastVisible,
          searchPageIndex: 0,
        };
      });
    }
  }, [
    debouncedSearchTerm,
    state.allParticipants,
    state.collectionName,
    state.participants,
    state.totalParticipants,
    state.selectedCategory,
    state.categoriesLoaded,
  ]);

  const loadMore = useCallback(() => {
    if (debouncedSearchTerm) {
      if (!state.fullFilteredList.length) return;
      setState((prev) => {
        const currentIndex = searchPageIndexRef.current + 1;
        const startIndex = currentIndex * PAGE_SIZE;
        const additionalList = prev.fullFilteredList.slice(
          startIndex,
          startIndex + PAGE_SIZE
        );
        if (!additionalList.length) return { ...prev, hasMore: false };
        searchPageIndexRef.current = currentIndex;
        return {
          ...prev,
          filteredParticipants: [
            ...prev.filteredParticipants,
            ...additionalList,
          ],
          hasMore: startIndex + PAGE_SIZE < prev.fullFilteredList.length,
          searchPageIndex: currentIndex,
          lastVisible: null,
        };
      });
    } else {
      if (state.loading || state.loadingMore || !state.hasMore) return;
      let nextLastVisible = state.lastVisible;
      if (!nextLastVisible && state.participants.length > 0) {
        nextLastVisible =
          state.participants[state.participants.length - 1]?.docRef || null;
      }
      if (!nextLastVisible) return;
      loadPage(
        state.collectionName,
        nextLastVisible,
        state.sortGenderDirection,
        state.selectedCategory
      );
    }
  }, [
    debouncedSearchTerm,
    state.fullFilteredList,
    state.lastVisible,
    state.loading,
    state.loadingMore,
    state.collectionName,
    state.sortGenderDirection,
    state.hasMore,
    state.participants,
    state.selectedCategory,
  ]);

  const handleSearchChange = useCallback((e) => {
    const searchValue = e.target.value || "";
    setState((prev) => {
      searchPageIndexRef.current = 0;
      return {
        ...prev,
        searchTerm: searchValue,
        lastVisible: searchValue ? null : prev.lastVisible,
        hasMore: true,
        searchPageIndex: 0,
      };
    });
  }, []);

  const handleGenderSortToggle = useCallback(() => {
    const newDirection = state.sortGenderDirection === "desc" ? "asc" : "desc";
    if (!state.collectionName) return;
    setState((prev) => {
      searchPageIndexRef.current = 0;
      return {
        ...prev,
        sortGenderDirection: newDirection,
        lastVisible: null,
        hasMore: true,
        searchPageIndex: 0,
      };
    });
    loadPage(state.collectionName, null, newDirection, state.selectedCategory);
  }, [
    state.sortGenderDirection,
    state.collectionName,
    loadPage,
    state.selectedCategory,
  ]);

  const rangeText = `${state.filteredParticipants.length} of ${
    debouncedSearchTerm ? state.totalFiltered : state.totalParticipants
  } Participants`;

  if (state.error) {
    return (
      <section className="w-full min-h-screen flex items-center justify-center flex-col bg-white text-center px-4">
        <p className="text-lg text-gray-600 mb-4">{state.error}</p>
        <Link to="/kegiatan" className="text-lime-600 underline text-base">
          Back to Event List
        </Link>
      </section>
    );
  }

  return (
    <section className="w-full min-h-screen py-20 text-center text-lime-600 flex-col justify-center relative bg-white">
      <div className="relative z-1 container mx-auto px-4">
        <motion.div
          className="mb-6 flex flex-col gap-4 max-w-6xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: "easeInOut" }}
          viewport={{ once: true }}
        >
          <h3 className="text-lg md:text-xl font-black uppercase text-lime-600 text-center block md:hidden">
            JAKARTA OPEN TRAIL RUN
          </h3>
          <div className="relative w-full max-w-md mx-auto md:ml-0">
            <input
              type="text"
              placeholder="Search by name..."
              value={state.searchTerm}
              onChange={handleSearchChange}
              className="input w-full p-2 pl-10 border border-gray-300 rounded-xl focus:ring-2 focus:ring-lime-600 text-slate-800 text-sm"
              aria-label="Search participant by name"
            />
            <FontAwesomeIcon
              icon={faSearch}
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
            />
          </div>
          <div className="flex flex-col md:flex-row-reverse md:items-center md:justify-between gap-4">
            <h3 className="text-lg md:text-xl font-black uppercase text-lime-600 md:text-right hidden md:block">
              JAKARTA OPEN TRAIL RUN
            </h3>
            {state.categories.length > 1 && (
              <div className="flex flex-wrap gap-2">
                {state.categories.map((category) => (
                  <CategoryButton
                    key={category.distance}
                    category={category}
                    selectedCategory={state.selectedCategory}
                    onClick={handleCategoryChange}
                  />
                ))}
              </div>
            )}
          </div>
        </motion.div>
        <motion.div
          className="bg-white rounded-2xl border border-gray-200 text-left relative overflow-hidden max-w-6xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: "easeInOut" }}
          viewport={{ once: true }}
        >
          <div className="overflow-x-auto">
            <table className="w-full table-auto divide-y divide-gray-200">
              <thead>
                <tr className="bg-gray-100">
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                    Name
                  </th>
                  <th
                    className="px-4 py-3 text-left md:text-center text-xs font-medium text-gray-600 uppercase tracking-wider cursor-pointer hover:text-lime-600 hidden md:table-cell"
                    onClick={handleGenderSortToggle}
                    aria-label="Sort by gender"
                  >
                    <span className="flex items-center md:justify-center">
                      Gender
                      {state.sortGenderDirection && (
                        <FontAwesomeIcon
                          icon={
                            state.sortGenderDirection === "asc"
                              ? faSortUp
                              : faSortDown
                          }
                          className="ml-1 text-gray-500"
                        />
                      )}
                    </span>
                  </th>
                  <th className="px-4 py-3 text-left md:text-center text-xs font-medium text-gray-600 uppercase tracking-wider">
                    City
                  </th>
                  <th className="px-4 py-3 text-left md:text-center text-xs font-medium text-gray-600 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody>
                {state.loading ? (
                  <SkeletonRow />
                ) : state.filteredParticipants.length === 0 ? (
                  <tr>
                    <td
                      colSpan={4}
                      className="px-4 py-4 text-center text-sm text-gray-600"
                    >
                      {state.searchTerm
                        ? "No participants match the search."
                        : state.selectedCategory !== "all"
                        ? "No participants registered for this category yet."
                        : "No participants registered for this event yet."}
                    </td>
                  </tr>
                ) : (
                  state.filteredParticipants.map((participant) => (
                    <tr
                      key={participant.id}
                      className="border-b border-gray-200"
                    >
                      <td
                        className={`px-4 py-4 text-sm whitespace-nowrap md:whitespace-normal ${
                          participant.gender === "Perempuan"
                            ? "text-lime-600"
                            : "text-gray-600"
                        }`}
                      >
                        {participant.nama || "-"}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-600 hidden md:table-cell md:text-center">
                        {participant.gender || "-"}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-600 md:text-center">
                        {participant.kota || "-"}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-600">
                        {participant.statusPembayaran === "settlement" ? (
                          <FontAwesomeIcon
                            icon={faCheckCircle}
                            className="text-green-500 mx-auto block"
                            title="Settled"
                          />
                        ) : (
                          <FontAwesomeIcon
                            icon={faClock}
                            className="text-yellow-500 mx-auto block"
                            title="Pending"
                          />
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div className="flex justify-end items-center px-4 py-2">
            <span className="text-sm text-gray-600 mr-4">{rangeText}</span>
            <button
              onClick={loadMore}
              disabled={state.loading || state.loadingMore || !state.hasMore}
              className={`px-3 py-1 rounded-lg transition-colors duration-200 flex items-center gap-1
                ${
                  state.loading || state.loadingMore || !state.hasMore
                    ? "bg-gray-200 text-gray-600 cursor-not-allowed"
                    : "bg-lime-600 text-white hover:bg-lime-700"
                }`}
              aria-label={
                state.hasMore
                  ? "Load more participants"
                  : "End of participant list"
              }
            >
              <span>{state.hasMore ? "View more" : "End of participants"}</span>
              <FontAwesomeIcon icon={faChevronDown} />
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  );
});
